import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Trophy, Medal, Crown, X, User } from 'lucide-react';
import { getCustomerPoints } from '@/lib/db/collections/points';
import { getGroupMembers } from '@/lib/firebase/queries/group-members';
import { buildLeaderboard, leaderboardMemberId, type LeaderboardMember } from '@/lib/groups/groupLeaderboard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface GroupLeaderboardProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupLeaderboard({ groupId, open, onOpenChange }: GroupLeaderboardProps) {
  const { t, dir, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardMember[]>([]);

  useEffect(() => {
    if (open && groupId) {
      loadLeaderboardData();
    }
  }, [open, groupId]);

  const loadLeaderboardData = async () => {
    try {
      setLoading(true);
      
      // ✅ Use reward points system for personal group leaderboards.
      // getGroupMembers already carries each member's users data, keyed by
      // `id` (the uid) — there is no `userId` on its rows.
      const groupMembers = await getGroupMembers(groupId);
      
      // Get reward points for each member
      const pointsById = new Map<string, number>();
      await Promise.all(
        groupMembers.map(async (member) => {
          const memberId = leaderboardMemberId(member);
          if (!memberId) return;
          try {
            const pointsData = await getCustomerPoints(memberId);
            pointsById.set(memberId, pointsData?.totalPoints || 0);
          } catch (memberError) {
            console.warn(`Error loading points for member ${memberId}:`, memberError);
          }
        })
      );
      
      // Sort by reward points descending and assign ranks
      const sortedMembers = buildLeaderboard(groupMembers, pointsById);
      
      console.log('✅ Loaded reward points leaderboard:', sortedMembers);
      setLeaderboardData(sortedMembers);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get rank icon based on position
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Trophy className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-screen h-[100dvh] sm:w-[90vw] sm:max-w-4xl sm:h-[80vh] flex flex-col"
        hideCloseButton
        dir={dir}
      >
        <DialogHeader className="pb-4 px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-primary" />
              <DialogTitle className="text-xl">
                {t('support.leaderboard')}
              </DialogTitle>
            </div>
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common.close')}
              >
                <span className="sr-only">{t('common.close')}</span>
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 space-y-3">
          {loading ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-3 w-[60px]" />
                </div>
                <Skeleton className="w-16 h-8" />
              </div>
            ))
          ) : leaderboardData.length > 0 ? (
            // ✅ Simplified leaderboard showing rank, member, and points
            leaderboardData.map((member) => (
              <div
                key={member.id}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-lg transition-colors",
                  member.rank <= 3 
                    ? "bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20" 
                    : "bg-muted/50 hover:bg-muted/70"
                )}
              >
                {/* Rank Icon */}
                <div className="flex items-center justify-center w-10">
                  {getRankIcon(member.rank)}
                </div>

                {/* Member Avatar & Name */}
                <div className="flex items-center gap-3 flex-1">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={member.photoURL} alt={member.name} />
                    <AvatarFallback className="bg-[#7dda55] text-white">
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="font-medium text-foreground">
                    {language === 'ar' ? (member.nameAr || member.name) : (member.nameEn || member.name)}
                  </div>
                </div>

                {/* Reward Points */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {member.rewardPoints}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('support.rewardPoints')}
                  </div>
                </div>
              </div>
            ))
          ) : (
            // No data message
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{t('support.noLeaderboardData')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 